import {GetServerSidePropsContext} from 'next'

import {brands, defaultBrandCode, getBrandByCode} from '../../../brand/config'
import {Brand, BrandCode} from '../../../brand/types'
import {checkIsBrandCheckMyBus, checkIsBrandKiwi} from '../../../brand/utils'
import {getUrlParam} from '../../../utils/url'
import {isBrowser} from '../../utils/environment'
import {warnServerEnvironment} from '../../utils/logging/server-warning'
import {
  getBrandOverrideFromCookie,
  persistBrandOverrideOnCookie
} from '../business/persistData'
import {shouldAllowOverrides} from './overrides'

/**
 * Log an error message to the console
 *
 * This function is used to log messages to the console. Those messages will end up in DataDog.
 * Once DataDog infra is implemented, we can remove this function and use DataDog directly.
 * @param message - The message to log
 */
export const logMessage = (message: string): void => {
  console.error(message)
}

/**
 * Find brand code by exact domainCode match
 * @param hostnamePart - The hostname part to match against domainCode
 * @param brands - The brands record to search in
 * @returns The matching brand code or null if not found
 */
export const findBrandByDomainCode = (
  hostnamePart: string,
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  const brandEntries = Object.entries(brands) as Array<[BrandCode, Brand]>
  const match = brandEntries.find(
    ([, brand]) => brand.domainCode === hostnamePart
  )
  return match ? match[0] : null
}

/**
 * Find brand code by substring match
 * @param hostnamePart - The hostname part to search for brand code substrings
 * @param brands - The brands record to search in
 * @returns The matching brand code or null if not found
 */
export const findBrandByCodeSubstring = (
  hostnamePart: string,
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  const brandCodes = Object.keys(brands) as BrandCode[]
  return brandCodes.find(code => hostnamePart.includes(code)) || null
}

/**
 * Apply brand resolution strategy to hostname parts
 * @param hostnameParts - Array of hostname parts to search through
 * @param brands - The brands record to search in
 * @returns The resolved brand code or null if not found
 */
const resolveBrandFromHostnameParts = (
  hostnameParts: string[],
  brands: Record<BrandCode, Brand>
): BrandCode | null => {
  // Strategy 1: Find first part that matches a domainCode exactly
  for (const part of hostnameParts) {
    const domainMatch = findBrandByDomainCode(part, brands)
    if (domainMatch) return domainMatch
  }

  // Strategy 2: Find first part that contains a brand code
  for (const part of hostnameParts) {
    const codeMatch = findBrandByCodeSubstring(part, brands)
    if (codeMatch) return codeMatch
  }

  return null
}

/**
 * Get the brand code from the hostname
 * @param hostname - The hostname to get the brand code for
 * @param brands - The brands to check against
 * @returns The brand code
 */
export const getBrandCodeByHost = (
  hostname: string,
  brands: Record<BrandCode, Brand>
): BrandCode => {
  const hostnameParts = hostname.split('.')
  const resolvedBrand = resolveBrandFromHostnameParts(hostnameParts, brands)

  if (resolvedBrand) return resolvedBrand

  if (!hostname.startsWith('localhost')) {
    logMessage(
      `Brand resolution failed. No domain match found. Hostname: ${hostname}`
    )
  }

  // Falling back to default brand code when no match is found
  return defaultBrandCode
}

/**
 * Get a a brand from the brandOverride param in the url
 * @param url - The url to get the brand for
 * @returns The brand for the url
 */
export const getBrandOverrideFromUrl = (url: string): Brand | null => {
  const hasBrandOverrideParam =
    url.indexOf('?') !== -1 && url.indexOf('brandOverride') !== -1
  if (!hasBrandOverrideParam) return null

  const search = url.split('?')[1]
  const brandOverride = getUrlParam({
    search,
    param: 'brandOverride'
  })
  return brandOverride ? getBrandByCode(brandOverride as string) : null
}

/**
 * Retrieves the brand code from the URL parameter 'brandOverride' or cookies for brand customization.
 * The override only works in non-production environments and demo sites.
 *
 * Behavior:
 * - get URL parameter and format: ?brandOverride=brandCode
 * - or get cookie name: 'brandOverride'
 * - if demo site or non-production environment, use the override brand code
 * - if override value is invalid, fall back to default brand ('vio')
 * - Persists override in cookie for consistent experience across page reloads
 * @param appEnv - The consumer process environment (e.g., 'development', 'staging', 'production', 'prd')
 * Warning: Don't use process.env.NODE_ENV, use the APP_ENV or equivalent from the env files on each package (accessed through Settings).
 * @returns brandCode - The determined brand code based on override or default.
 */
export const getBrandCodeFromUrlOrCookie = (appEnv?: string) => {
  if (!isBrowser) {
    warnServerEnvironment(`getBrandCodeFromUrlOrCookie`)
    return defaultBrandCode
  }

  const {hostname} = location
  if (!hostname) return defaultBrandCode

  const brandOverride =
    (getUrlParam({
      search: location.search,
      param: 'brandOverride'
    }) as BrandCode) || getBrandOverrideFromCookie()

  // Use unified environment checking logic (same as hooks)
  const brandOverrideIsValid =
    shouldAllowOverrides(appEnv || '', hostname) &&
    Boolean(brandOverride) &&
    Boolean(brands[brandOverride])

  const brandCode = getBrandCodeByHost(hostname, brands)

  if (brandOverrideIsValid) {
    persistBrandOverrideOnCookie(brandOverride)
    return brandOverride
  }

  return brandCode
}

/**
 * Retrieves the URL for the header logo.
 * Some brands can have a custom logo URL set in the brands config.
 * There are brands that have a custom logic for the logo URL. This is covered  by the function too
 *
 * This function must be called only inside useEffect!
 * @param brand - The brand object containing brand configuration.
 * @param logoUrl - The custom logo URL to be used if provided.
 * @returns string - The URL for the header logo.
 */
export const getBrandHeaderLogoUrl = (brand: Brand, logoUrl?: string) => {
  const brandIsCheckMyBus = checkIsBrandCheckMyBus(brand)
  if (!brandIsCheckMyBus) {
    return brand.siteAddresses.logoRedirectUrl || logoUrl
  }

  const hostParts = location.host.split('.')
  const checkMyBusHost =
    hostParts.length > 2 ? hostParts.slice(1).join('.') : location.host
  return `${location.protocol}//${checkMyBusHost}`
}

/**
 * Check if the brand is kiwi and return a different logo height
 * @param isCompact - Whether the header is compact
 * @param brand - The brand object containing brand configuration.
 * @returns The logo height
 */
export const getLogoHeight = (brand: Brand, isCompact?: boolean) => {
  if (checkIsBrandKiwi(brand)) {
    return 40
  }
  return isCompact ? 'auto' : undefined
}

/**
 * Get the brand from the server-side context
 * To be used in getServerSideProps
 * @param context - Next.js getServerSideProps context.
 * @returns The determined Brand object.
 */
export const getBrandFromServerContext = (
  context: GetServerSidePropsContext
): Brand => {
  const {req, resolvedUrl} = context

  // On Staging and Production, the host header is set by a lambda function
  // and the host header is not the actual host
  const host = (req.headers['x-host'] as string) || req.headers.host!

  const brandCode = getBrandCodeByHost(host, brands)
  const brand = getBrandByCode(brandCode)

  if (!brand) {
    logMessage(
      `Brand resolution failed. No brand config found for brand code: ${brandCode}. Hostname: ${host}`
    )
  }

  if (brand.code === 'demo' || process.env.NEXT_PUBLIC_ENV !== 'production') {
    const brandOverride =
      getBrandOverrideFromUrl(resolvedUrl!) ||
      (req.cookies.brandOverride && getBrandByCode(req.cookies.brandOverride))

    if (brandOverride) return brandOverride
  }

  return brand
}
